import type { TutorialContent } from './types'

export const EN: TutorialContent = {
    ui: {
        title: 'Tutorial',
        subtitle: 'Every tool step by step – with tips and fixes',
        searchPlaceholder: 'Search the tutorial …',
        noResults: 'No matches – try another word.',
        openTool: 'Open tool',
        time: 'Duration of one run',
        whenToUse: 'When to use it',
        whatYouGet: 'The result',
        steps: 'Step by step',
        options: 'Fields and options',
        behindTheScenes: 'Behind the scenes: how the tool works',
        tips: 'Tips from practice',
        troubleshooting: 'When something goes wrong',
        related: 'Works well with',
        next: 'Next',
        glossaryFilter: 'Search a term …',
    },

    nav: {
        start: 'Start here',
        groups: [
            { label: 'Overview & automatic', tools: ['breaking', 'morning_briefing', 'infographics', 'newsflash'] },
            { label: 'Articles', tools: ['generator'] },
            { label: 'Short news', tools: ['headlines', 'crypto_headlines', 'single_headline'] },
            { label: 'Earnings', tools: ['revenue'] },
            { label: 'Check & enrich', tools: ['formalia', 'seo', 'find_similar'] },
        ],
        reference: 'Reference',
        pages: {
            welcome: 'Welcome',
            choose: 'Which tool?',
            workflow: 'A typical day',
            glossary: 'Glossary',
            troubleshooting: 'Troubleshooting',
        },
    },

    welcome: {
        title: 'Welcome to HKCM News',
        intro:
            'HKCM News finds current financial news and turns it into German texts in the HKCM style – from a newsflash to a full, fact-checked article. Every result is a draft for the desk: review it, adjust it, export it. This tutorial explains each tool step by step; pick a topic on the left or search everything above.',
        highlightsTitle: 'The key tools',
        highlights: [
            { tool: 'newsflash', text: 'Newsflashes that write themselves – every three hours from 7 am to 7 pm, without duplicates.' },
            { tool: 'morning_briefing', text: 'The morning page opens on its own – and the day’s stories become one article with pictures.' },
            { tool: 'generator', text: 'A finished article from a link, a topic or your own text in two to four minutes – fact-checked.' },
            { tool: 'revenue', text: 'Quarterly results with analyst estimates, a results table and TradingView.' },
            { tool: 'formalia', text: 'Check any text against the HKCM rules before it goes out.' },
        ],
        quickStartTitle: 'Get started in three steps',
        quickStart: [
            'Find the right tool: look up your task under “Which tool?” or pick a tool on the left.',
            'Follow the guide: every page explains the steps, all fields and common problems. “Open tool” takes you straight there.',
            'Review and export: check numbers, names and quotes against the source, check the formalia, then export to Word.',
        ],
        rulesTitle: 'Ground rules',
        rules: [
            {
                name: 'Everything is a draft',
                desc: 'The tools check facts and formalia, but they don’t replace editorial sign-off. Before publishing, check numbers, names and quotes against the source.',
            },
            {
                name: 'No investment advice',
                desc: 'No personal calls to buy or sell (“you should buy now”) and no claims that something “suits you”. Assessments stay factual and about the instrument. The tools follow this – please do too when editing.',
            },
            {
                name: 'HKCM style',
                desc: 'German, factual and explanatory. No gendered forms, “Prozent” instead of %, “US-Dollar” and “Euro” instead of symbols. A point as decimal separator, as hkcm.com publishes (“2.5 Prozent”, “93.65 US-Dollar”), thousands from five digits separated by a space (“86 000”), four-digit numbers written together (“7764”). The dash is “ – ”, never “—” or “ - ”; in generated texts the tools set it themselves. The Formalia Check tests all of this automatically.',
            },
            {
                name: 'Save before reloading',
                desc: 'Every tool keeps its state while the app is open – also when you switch to another tool and back, and a running job keeps going. Reloading or closing the browser loses whatever is not exported yet. Exception: Newsflash stores everything on the server. Export first.',
            },
            {
                name: 'Sidebar',
                desc: 'All tools are on the left, grouped by task. ⌘B (Mac) or Ctrl+B (Windows), or the button at the top, collapses it to icons – hover an icon to see its name. The address bar remembers the open tool (e.g. …/#/breaking-news): reloading or a bookmark opens that tool directly.',
            },
            {
                name: 'Blocked pages',
                desc: 'Some sites block automated access or sit behind a paywall. Paste the text yourself instead (Article Generator: “Manuell”, manual) or use another source.',
            },
            {
                name: 'Language',
                desc: 'The interface is in German; only the tool names stay English. The texts are always written in German. This tutorial is available in German and English – switch at the top right with “Deutsch” / “English”.',
            },
        ],
    },

    choose: {
        title: 'Which tool do I need?',
        intro: 'Sorted by task – a click on the tool opens its guide.',
        rows: [
            { need: 'A steady stream of newsflashes without lifting a finger', tool: 'newsflash', why: 'Runs every three hours on its own and skips what has already been published.' },
            { need: 'See what’s moving the markets right now', tool: 'breaking', why: 'The top headlines per section, ranked and translated – ideal for finding topics.' },
            { need: 'Prepare the morning page', tool: 'morning_briefing', why: 'Five sections on opening, plus one article from the stories you pick – with pictures, editable, as Word.' },
            { need: 'A finished article from a link or topic, fast', tool: 'generator', why: 'One click, two to four minutes, with source research, fact-check and Word export.' },
            { need: 'Turn quarterly results into an article', tool: 'revenue', why: 'Figures from the report, analyst estimates and a results table.' },
            { need: 'Many short news items on one topic', tool: 'headlines', why: 'German short news from a Google News topic page, often around 50 per run.' },
            { need: 'Crypto short news', tool: 'crypto_headlines', why: 'No link needed: Google News Crypto, CoinDesk and The Block.' },
            { need: 'Condense a single story', tool: 'single_headline', why: 'Headline and summary in 10 to 30 seconds.' },
            { need: 'Check a text against the HKCM rules', tool: 'formalia', why: 'Lengths, formats and gendered language – with Bestanden, FAIL and WARN.' },
            { need: 'Improve a text for Google', tool: 'seo', why: 'Suggestions by priority, applied automatically if you want.' },
            { need: 'Add fitting links', tool: 'find_similar', why: 'HKCM archive, HKCM products, the web and the German press – inserted by AI.' },
            { need: 'Graphics for an article', tool: 'infographics', why: 'Infographics in the HKCM design as PNG in 4K, 3840 × 2160 pixels.' },
        ],
    },

    workflow: {
        title: 'A typical day',
        intro: 'How the tools fit together day to day – a suggestion, not a rule.',
        blocks: [
            {
                when: 'From 7 am',
                title: 'Start the day',
                items: [
                    { tool: 'newsflash', text: 'The first automatic run is at 7 am – skim what happened overnight.' },
                    { tool: 'morning_briefing', text: 'Open it before 9 am so winners and losers compare cleanly with the previous day. Tick stories and click “Artikel schreiben”.' },
                    { tool: 'crypto_headlines', text: 'A crypto roundup for the morning, if needed.' },
                ],
            },
            {
                when: 'During the day',
                title: 'Pick up the news',
                items: [
                    { tool: 'breaking', text: 'Scan topics. Newsflash adds to itself at 10 am, 1 pm and 4 pm.' },
                    { tool: 'generator', text: 'Turn a story into an article quickly – take the link from Breaking News or Newsflash.' },
                    { tool: 'single_headline', text: 'Single stories for social media or push.' },
                ],
            },
            {
                when: 'In earnings season',
                title: 'Quarterly results',
                items: [
                    { tool: 'revenue', text: 'Load the press release or PDF, check the figures, export with or without TradingView.' },
                ],
            },
            {
                when: 'Before publishing',
                title: 'Quality control',
                items: [
                    { tool: 'formalia', text: 'Check the formalia until no FAIL findings are left.' },
                    { tool: 'seo', text: 'Apply SEO suggestions – “Kritisch” (critical) first.' },
                    { tool: 'find_similar', text: 'Add one or two internal HKCM links and fitting products.' },
                    { tool: 'infographics', text: 'Add graphics if useful.' },
                    { text: 'Finally, check numbers against the source and export to Word.' },
                ],
            },
            {
                when: '7 pm',
                title: 'End of day',
                items: [
                    { tool: 'newsflash', text: 'Last automatic run. For a daily recap choose “Nur heute” and export to Word; items stay available for three days.' },
                ],
            },
        ],
    },

    tools: {
        breaking: {
            title: 'Breaking News',
            tagline: 'The top headlines from Google News by section – filtered, de-duplicated, ranked by importance and in German.',
            badge: 'Live overview',
            time: '10–20 s per section',
            whenToUse: [
                'In the morning and during the day, to see what’s moving the markets.',
                'To find topics for an article – the links go straight into the Article Generator.',
                'To check one area, such as Germany, crypto or commodities.',
            ],
            whatYouGet: [
                'Up to 15 headlines per section, in German and ranked by importance (rank 1 is the most important story).',
                'The source and the story’s age (“vor 3 Std.”, 3 hours ago) under each headline – one click opens the original article in a new tab.',
                'Loaded sections stay available while the tool is open (green dot on the section).',
            ],
            steps: [
                {
                    title: 'Choose a section',
                    desc: 'Choose a section at the top: “Top-Meldungen” (top stories), “USA”, “Deutschland”, “Welt” (world), “Wirtschaft” (business), “Technologie”, “Krypto” or “Rohstoffe” (commodities). The section loads right away.',
                },
                {
                    title: 'Wait a moment',
                    desc: 'The progress bar shows what’s happening: fetching, filtering, ranking, translating. After 10 to 20 seconds the list is ready.',
                },
                {
                    title: 'Read the list',
                    desc: 'Stories are ordered by rank. Click the source under a headline to open the original article.',
                },
                {
                    title: 'Load all sections (optional)',
                    desc: '“Alle laden” (load all) loads every section that’s still missing, four at a time. Sections already loaded are skipped; any that could not be loaded are named in the error message afterwards.',
                },
                {
                    title: 'Refresh',
                    desc: '“Alle aktualisieren” (refresh all) reloads every section; the current lists stay until the new ones arrive. Clicking the open section again reloads just that one; clicking another loaded section shows its stored list.',
                },
            ],
            options: [
                { name: 'Top-Meldungen', desc: 'The day’s most important news, filtered for relevance to markets and business.' },
                { name: 'USA / Welt', desc: 'US or international news, also filtered for market relevance.' },
                { name: 'Deutschland', desc: 'German business news from German-language sources; not translated.' },
                { name: 'Wirtschaft / Technologie', desc: 'Business or technology. These sources count as relevant and aren’t filtered further.' },
                { name: 'Krypto / Rohstoffe', desc: 'Cryptocurrencies, or commodities such as gold, oil and silver; commodity stories are filtered for market relevance.' },
                { name: '“Alle laden”', desc: 'Loads all missing sections, four at a time.' },
                { name: '“Alle aktualisieren”', desc: 'Reloads all sections; the old lists stay visible until then.' },
            ],
            behindTheScenes: [
                'The source is the section’s Google News feed. If it returns nothing, GPT finds the current headlines with a web search.',
                'For Top-Meldungen, USA, Welt, Deutschland and Rohstoffe, a fast model checks every story for relevance to markets and business.',
                'Stories older than 36 hours are dropped, and ranking takes into account how fresh a story is.',
                'Another pass merges duplicate stories and ranks by importance; the best 15 are translated into German.',
                'Nothing is stored: leave the tool and the lists are gone.',
            ],
            tips: [
                'A good starting point for articles: copy a story’s link and paste it into the Article Generator in “URL” mode – Google News links work there directly.',
                'Refreshing every 30 to 60 minutes is enough; new events take a while to show up in the feeds.',
                'For newsflashes without clicking, use Newsflash – it runs automatically every three hours.',
            ],
            troubleshooting: [
                {
                    q: '“Keine Meldungen gefunden.” (no stories found)',
                    a: 'Nothing came back for this section just now. Wait a few minutes and try again, or pick another section.',
                },
                {
                    q: '“Anmeldung abgelaufen” (sign-in expired)',
                    a: 'Your sign-in has expired. Click “Erneut anmelden” (sign in again) in the red notice at the top and sign in in the window – the page and running jobs stay. If no window opens, allow pop-ups for the site, or click “Abmelden” and sign in again.',
                },
                {
                    q: 'The list is empty after reloading',
                    a: 'Lists are kept while the app is open – also when you switch tools. After a reload, just load the section again.',
                },
            ],
            related: ['newsflash', 'generator', 'morning_briefing'],
        },

        morning_briefing: {
            title: 'Morning Briefing',
            tagline:
                'The morning page opens on its own – the stories you pick become one article with pictures, editable and exported to Word.',
            badge: 'Daily draft',
            time: 'approx. 40 s, article 2–3 min',
            whenToUse: [
                'Every morning: the page builds the briefing when it opens – or shows the one a colleague built in the last half hour.',
                'To write the day’s article from one or several stories – or a topic of your own.',
                'When the article is written elsewhere: export just the sections with “Briefing als Word”.',
            ],
            whatYouGet: [
                'Five sections: 1 · Breaking News, 2 · HKCM winners and losers, 3 · The most exciting topics, 4 · HKCM trades, 5 · HKCM signal.',
                'A “Hinweise zur Prüfung” (review notes) box listing everything missing or not from today.',
                'One article from up to five stories or a topic of your own, laid out like the published Morning Briefings: “Guten Morgen!”, two to four sections, a closing paragraph on the markets, 700 to 900 words. The first mention of an HKCM stock links its package.',
                'Its pictures, on their own: a photoreal cover and an HKCM explainer graphic in 4K with a caption for each section – more graphics on request.',
                'Under the article, as on hkcm.com: HKCM winners and losers, “Die spannendsten Themen” (three current hkcm.com articles), HKCM trades and the HKCM signal – each block with “Im Word-Export” (in the Word export).',
                'A Word export of the article (HKCM_Morning_Briefing_Artikel_<date>.docx) with cover, graphics, SEO block (meta and Open Graph texts, category, tags) and sources, in German or English. The briefing itself is still there as “Briefing als Word” and as Markdown.',
            ],
            steps: [
                {
                    title: 'Open the page',
                    desc: 'The briefing builds itself in about 40 seconds and shows its progress; “Abbrechen” stops the run. If a colleague built one in the last half hour, it appears at once. “Neu erstellen” builds it again with the options set.',
                },
                {
                    title: 'Read the review notes first',
                    desc: '“Hinweise zur Prüfung” lists what’s missing or may be outdated – for example an HKCM signal from the previous day (“Nicht von heute:”) or quotes that already show the current day after 9 am.',
                },
                {
                    title: 'Tick stories or enter a topic of your own',
                    desc: 'Tick up to five stories in section 1 – they are combined into one article. “Eigenes Thema (optional)” (own topic) takes something else, instead of the selection or on top. “Zusätzliche Anweisung (optional)” (extra instruction) sets an angle, e.g. “focus on the consequences for European investors”.',
                },
                {
                    title: 'Click “Artikel schreiben”',
                    desc: 'Research and writing take two to three minutes; “Abbrechen” stops them. Then the pictures come on their own, in one to two minutes. A new article replaces the one on the page only after asking.',
                },
                {
                    title: 'Click “Bearbeiten”',
                    desc: 'Headline (H1), teaser, key points, intro, every section, the market close and the SEO fields become input fields – with character counts and target ranges. “Fertig” re-checks the formalia of the changed text and shows the article as it will appear again; “Zurücksetzen” brings back the written version, the pictures stay.',
                },
                {
                    title: 'Adjust the pictures',
                    desc: 'Under each picture: “Neu erstellen” redraws it in a different arrangement, “Entfernen” takes it out. “Grafik hinzufügen” under a section adds another graphic – optionally with “Was soll die Grafik zeigen?” (what should it show) – one paragraph further down; up to four per section. “Alle Bilder neu erstellen” redraws the cover and each section’s first graphic; “Stil” beside it picks the graphics’ look – automatic, light or dark.',
                },
                {
                    title: 'Choose the blocks under the article',
                    desc: 'HKCM winners and losers, “Die spannendsten Themen”, HKCM trades and HKCM signal each have “Im Word-Export”. HKCM trades is off because the published briefings show no trades; so is a signal that is not from today. Trades and signal come from the package of the article’s subject, which the “Thema des Tages” takes over on its own.',
                },
                {
                    title: 'Export',
                    desc: 'In the article’s header, “Word” downloads the Word file with “DE” and the English translation with “EN”. If you write the article yourself, use “Briefing als Word” at the top: the sections without an article, HKCM trades and HKCM signal as the page shows them, the moves in house style (“+5.83”). “Markdown” copies the briefing as it was built.',
                },
                {
                    title: 'Change the package for trades and signal (optional)',
                    desc: 'Enter a “Thema des Tages” and press Enter, pick an “HKCM-Paket” or click one under “Passt auch:”: sections 4 and 5 reload, the rest of the briefing and the article stay.',
                },
            ],
            options: [
                { name: 'Thema des Tages', desc: 'Keyword that picks the HKCM package for HKCM trades and HKCM signal. Left empty, it takes the article’s main subject – and the next article’s; one you typed stays. Enter reloads sections 4 and 5.' },
                { name: 'HKCM-Paket', desc: '“Automatisch – passend zum Thema” or a fixed package.' },
                { name: 'Breaking News', desc: 'How many stories section 1 shows (1–10, default 5). Applies from “Neu erstellen”.' },
                { name: 'Gewinner / Verlierer', desc: 'How many stocks per side section 2 shows (1–10, default 3).' },
                { name: 'HKCM-Themen', desc: 'How many topics section 3 shows (1–10, default 3).' },
                { name: 'Eigenes Thema', desc: 'A topic the article is researched on – instead of the selected stories or on top.' },
                { name: 'Zusätzliche Anweisung', desc: 'Applies to the article, e.g. an angle or a target audience.' },
                { name: '“Grafik hinzufügen”', desc: 'Another explainer graphic for a section, with a different arrangement and a caption of its own. “Was soll die Grafik zeigen?” sets its focus; every figure must appear in the section. At most four graphics per section; a section without text gets none. All graphics in 4K with the HKCM logo, in the style of the graphics on hkcm.com: light like the Morning Briefing graphics or as a dark poster like the crypto and crash series. With “Stil: automatisch” the model suggests the look per article; every graphic of an article keeps it, redrawn and added ones too.' },
            ],
            behindTheScenes: [
                'Section 1: current stories from Google News – from the last day, on Mondays including the weekend – grouped into stories and ranked by importance for the HKCM universe. “N Medien” counts distinct outlets.',
                'Section 2: prices of the roughly 180 stocks on the HKCM watchlist on TradingView – the biggest winners and losers.',
                'Sections 3 to 5: posts, trades and announcements from HKCM content. Trades only appear after a five-day embargo; the HKCM signal rewrites the previous day’s announcement for today. Since HKCM’s relaunch on 18.09.2026 that announcement no longer exists: the signal then names the analyses HKCM publishes in the package on that weekday – by the weekly plan (“Nach Wochenplan:”, check against today’s plan), otherwise the package’s latest analyses with their date. Also since 18.09.2026 the database files trades under the stock instead of the package – they are placed through earlier HKCM messages and the product catalogue and marked “zugeordnet” (placed).',
                'The model only selects from the data it’s given and never invents sources. If a section fails, the rest stays and the failure is listed in the notes.',
                'The article: GPT-6 researches every story with web search and combines them into one text laid out like the published Morning Briefings; it verifies its sources and follows the formalia. The links to HKCM packages come from the product catalogue, set by the program, not the model.',
                '“Die spannendsten Themen”: three current articles from the hkcm.com archive. The model only picks – headlines and links come from the archive.',
                'Graphics: a model writes each graphic’s text from the section, and every figure must appear there. The image model draws the graphic, a second model proofreads it and has it redrawn once on a spelling mistake. No two graphics of an article share an arrangement; the HKCM logo is inserted afterwards, never drawn.',
            ],
            tips: [
                'Open it before 9 am – then section 2 compares cleanly with the previous day.',
                'Several stories on one theme, say the Fed, the yen and bitcoin, become one article with a section per story.',
                'Reloading is fine: the article comes back with all your changes and pictures, a run still writing is followed up, and HKCM trades and HKCM signal load for its subject – in this browser, for today. “Verwerfen” in the notice takes it off the page.',
            ],
            troubleshooting: [
                {
                    q: 'Sections 4 and 5 are empty',
                    a: 'They belong to the package of the day’s topic and appear with the article. Without an article, enter a “Thema des Tages” and press Enter, or pick a package.',
                },
                {
                    q: '“HKCM-Inhalte sind nicht konfiguriert …”',
                    a: 'The server has no access to HKCM content, so sections 3 to 5 stay empty. Breaking news and winners/losers still work. Please tell the tech team.',
                },
                {
                    q: '“Der Briefing-Auftrag ist nicht mehr erreichbar.”',
                    a: 'The server restarted during the run. Create the briefing again.',
                },
                {
                    q: 'The article failed',
                    a: 'The selection is still there – click “Artikel schreiben” once more.',
                },
                {
                    q: '“zugeordnet” next to a trade',
                    a: 'The HKCM database delivered the trade without a package; it was placed through earlier messages. Check it belongs to the package before publishing – the notes name stocks that several packages hold.',
                },
                {
                    q: '“Bitte die Schreibweise prüfen: …” under a graphic',
                    a: 'The proofreading found a word that is not in the graphic as planned, even after the second attempt. Look at the graphic and click “Neu erstellen” if it is wrong.',
                },
                {
                    q: '“Die Schreibweise wurde nicht mehr gegengelesen …” under a graphic',
                    a: 'The graphic was finished so late that there was no time left to proofread it. Read its texts yourself and click “Neu erstellen” if one is wrong.',
                },
                {
                    q: '“Das Bild wurde nicht rechtzeitig fertig …”',
                    a: 'Click “Neu erstellen” under the missing picture – the other pictures stay.',
                },
                {
                    q: '“In einem anderen Tab gespeichert”',
                    a: 'The article is open in two tabs and the other one saved a newer version. This page then stops saving, so that version is not lost. “Neueren Stand laden” (load the newer version) takes over the other tab’s version, “Diesen Stand behalten” (keep this version) saves this page’s version over it.',
                },
            ],
            related: ['breaking', 'newsflash', 'generator'],
        },

        newsflash: {
            title: 'Newsflash',
            tagline:
                'Automatic newsflashes: every three hours from 7 am to 7 pm, new breaking and crypto stories become short German texts – nothing twice, available for three days.',
            badge: 'Automatic',
            time: 'runs by itself · manual up to approx. 1 min',
            whenToUse: [
                'When you need a steady stream of short newsflashes without triggering anything.',
                'To see quickly what’s new since you last looked.',
                'As a template for the CMS, newsletters or social media.',
            ],
            whatYouGet: [
                'A feed grouped by day: time, a “Breaking News” or “Krypto” label, German headline, three sentences and the source.',
                'Up to twelve breaking and six crypto items per run – as many as there are important stories, never filler.',
                'A Word export for today or the last three days, optionally for one section only – in German or English.',
            ],
            steps: [
                {
                    title: 'Open Newsflash',
                    desc: 'The feed appears right away. The top shows when the last run was and how many items it brought (“Letzter Lauf: … – 3 neu”), and when the next one starts.',
                },
                {
                    title: 'Filter',
                    desc: 'Choose “Alle”, “Breaking News” or “Krypto” – the number next to it shows how many items there are.',
                },
                {
                    title: 'Use an item',
                    desc: '“Kopieren” copies headline, text and source to the clipboard. The source link opens the original article.',
                },
                {
                    title: 'Check now (optional)',
                    desc: '“Jetzt prüfen” starts a run immediately, with a progress bar. If one is already running, the page shows “Ein Lauf ist gerade aktiv …” and refreshes itself afterwards.',
                },
                {
                    title: 'Download as Word',
                    desc: 'Choose the period (“Letzte 3 Tage” or “Nur heute”) and click “DE” or “EN” under “Word”. The currently selected section is exported.',
                },
            ],
            options: [
                { name: '“Jetzt prüfen”', desc: 'Starts a run right away, in addition to the scheduled ones.' },
                { name: 'Filter', desc: 'All, Breaking News or Krypto; also applies to the Word export.' },
                { name: 'Period', desc: '“Letzte 3 Tage” (last 3 days) or “Nur heute” (today only) for the Word export.' },
                { name: '“Word” – “DE” / “EN”', desc: 'Downloads HKCM_Newsflash_<date>.docx; “EN” downloads the English translation (…_EN.docx).' },
                { name: 'Status line', desc: 'Last run (with the number of new items, or “fehlgeschlagen” if it failed) and the next scheduled run.' },
            ],
            behindTheScenes: [
                'Scheduled at 7 am, 10 am, 1 pm, 4 pm and 7 pm German time – on the server, even when nobody has the page open. If a slot was missed, opening the page catches it up.',
                'Sources: the Breaking News Google News sections, plus Google News searches for markets, the economy and central banks, earnings, the stock exchange and crypto, and CoinDesk, The Block, Cointelegraph, Decrypt and Bitcoin Magazine. If Google News fails, GPT finds the stories with a web search.',
                'Only stories from the last 14 hours count. Identical links and headlines drop out immediately; a model also recognises the same story from different sources and anything already published.',
                'Each item is researched with web search and written in the HKCM style, without investment advice. If the event is more than two days old, it’s skipped for good.',
                'Only one run happens at a time. Items are kept for three days; older ones disappear automatically.',
                'What must not appear stays out: an item with a personal call to action, a figure a thousand times off, a “du” or gendered forms is not published – the next run tries it again.',
            ],
            tips: [
                '“Jetzt prüfen” is rarely needed – the feed fills itself.',
                'Check numbers and names against the source before publishing; the texts are drafts.',
                'For a full article, take the source link into the Article Generator.',
            ],
            troubleshooting: [
                {
                    q: '“Keine neuen Eilmeldungen seit dem letzten Lauf.”',
                    a: 'Nothing new came in, or everything new was already published. That’s normal – the next run checks again.',
                },
                {
                    q: '“Ein anderer Lauf ist gerade aktiv …”',
                    a: 'A scheduled run or a colleague just started one. Wait – the page refreshes as soon as it’s done.',
                },
                {
                    q: '“Im gewählten Zeitraum gibt es keine Meldungen.”',
                    a: 'There’s nothing to export for this period and section. Choose “Letzte 3 Tage” or the “Alle” filter.',
                },
                {
                    q: 'The status line says “fehlgeschlagen”',
                    a: 'The run stopped, for example because no source was reachable. The notes below give the reason. The next run tries again, or click “Jetzt prüfen”.',
                },
                {
                    q: 'Older items have disappeared',
                    a: 'By design: items are kept for three days. If you need them longer, export them to Word first.',
                },
                {
                    q: '“Der Lauf wurde unterbrochen, bevor ein Ergebnis kam.”',
                    a: 'The connection to the server ended before the result – a request may take two minutes at most. Click “Jetzt prüfen” again; items already published won’t appear twice.',
                },
            ],
            related: ['breaking', 'crypto_headlines', 'generator'],
        },

        generator: {
            title: 'Article Generator',
            tagline:
                'A URL, a search topic or your own text becomes a complete German HKCM article in two to four minutes – with source research, formalia check and fact-check.',
            badge: 'Complete article',
            time: 'approx. 2–4 min',
            whenToUse: [
                'When a story should become a finished article quickly.',
                'When several sources on one topic should be combined (input “Suchbegriff”, search query).',
                'When your own text, notes or a paywalled article should be put into the HKCM style (input “Manuell”, manual).',
            ],
            whatYouGet: [
                'Headline (H1), teaser, three “Kernpunkte” (key points), body text with subheadings and – with “FAQ erzeugen” – an FAQ.',
                'SEO fields: meta title, meta description, category (one of the seven CMS categories) and tags.',
                'A formalia badge (Bestanden, FAIL or WARN) with the key metrics.',
                'A “Faktencheck” (fact-check) card: “Geprüft – keine Beanstandungen” (checked – no issues) or the number of findings (“2 Hinweise”), each with its weight (Hoch, Mittel, Gering – high, medium, low), the claim and the reason – or “Nicht geprüft” (not checked) when the fact-check did not run.',
                'The sources used, listed on the right under “Quellen”.',
                'Optionally a cover image, news graphics and a sentiment analysis; export as a Word report.',
            ],
            steps: [
                {
                    title: 'Choose the input',
                    desc: 'On the left under “Eingabe”:\n• “URL” – a link to an article under “Artikel-URL”. Up to five links via “URL hinzufügen”; the icon in the field pastes from the clipboard.\n• “Suchbegriff” – a topic or question, e.g. “Warum fällt die Nvidia-Aktie heute?”.\n• “Manuell” – paste “Titel”, “Quellenlink” and the full “Artikeltext”; all three fields are required.',
                },
                {
                    title: 'Set options (optional)',
                    desc: '“Ausgabe” (output): “Vollständiger Artikel” (full article) or “Überblick (nur Kernfakten)” (overview). “Tonalität” (tone): leave empty for the neutral HKCM tone. Tick “FAQ erzeugen” (create FAQ) if the article should get an FAQ – only for “Vollständiger Artikel”, off by default. Tick “News-Grafiken erstellen” to add graphics.',
                },
                {
                    title: 'Click “Artikel erstellen”',
                    desc: 'The button sits at the bottom of the left panel. The progress bar names the step – “Quellensuche läuft …” (finding sources), “Artikel wird geschrieben …” (writing), “Faktencheck läuft …” (fact-check) – with the “Protokoll” (log) below. After two to four minutes it says “Fertig”. “Abbrechen” next to the bar cancels the run; if the server refuses, the page gives the reason and the run continues.',
                },
                {
                    title: 'Review the result',
                    desc: 'Start with the formalia badge: fix FAIL before publishing, WARN is a recommendation. Below it, the “Faktencheck” card: each finding names the claim and why it is doubtful – check it against the sources and correct it. Then read headline, teaser, key points, body, the FAQ if there is one, and “SEO und Metadaten”. The sources of the article are listed on the right under “Quellen”; “Quelle öffnen” opens the original.',
                },
                {
                    title: 'Edit',
                    desc: '“Bearbeiten” in the article card header makes headline, teaser, key points, body and FAQ editable. Select a passage in the headline or body, click “Mit KI bearbeiten”, enter an instruction (e.g. “kürzer und sachlicher”), click “Vorschlag erstellen” and, once checked, “Übernehmen”. “Rückgängig” reverts changes to the body, “Fertig” ends editing.',
                },
                {
                    title: 'Extras (optional)',
                    desc: 'Top right: “Titelbild erstellen” creates a cover image from headline and body; “Eigene Vorgaben (optional)” (own specifications) adds a wish (e.g. “Ölfass im Mittelpunkt”). “Neu erstellen” until it fits, then “Übernehmen”. “Stimmungsanalyse” (sentiment analysis) rates the market mood (Bullish, Bearish or Neutral, with affected stocks) and can be saved to Word. News graphics appear below the key points, each downloadable as PNG.',
                },
                {
                    title: 'Export',
                    desc: '“Word” (“DE” or “EN”) at the top right downloads everything for the CMS: headline, cover image, teaser, meta and social fields, “Das Wichtigste in Kürze”, body with graphics, FAQ (if created), SEO and the sources.',
                },
            ],
            options: [
                { name: 'URL', desc: 'Up to five article links. The first is the main source; the tool finds further reports on the topic itself.' },
                { name: 'Suchbegriff', desc: 'A topic or a specific question. The tool finds matching current reports itself – the more specific, the better.' },
                { name: 'Manuell', desc: 'For blocked pages, paywalls or your own texts: title, source link and full article text.' },
                { name: 'Ausgabe', desc: '“Vollständiger Artikel” writes the whole article. “Überblick (nur Kernfakten)” returns only main topic, tone, key facts and companies mentioned – good for sizing up a topic quickly.' },
                { name: 'Tonalität', desc: 'Optional tone; empty means the neutral HKCM tone. Example: “vorsichtig, mit Blick auf Risiken”.' },
                { name: 'FAQ erzeugen', desc: 'Only for “Vollständiger Artikel”, off by default: “Bis zu 4 Fragen mit Antworten für das FAQ-Feld im CMS.” (up to four questions with answers for the CMS FAQ field). Without the tick the article has no FAQ – neither on the page nor in the Word file.' },
                { name: 'News-Grafiken erstellen', desc: 'Creates infographics in the HKCM design after the article – “Automatisch (2 bis 3)” or 1 to 4, optionally with a source credit. They’re included in the Word report.' },
            ],
            behindTheScenes: [
                'Sources: the main source is read, via a browser or GPT research if the page blocks access. GPT then searches the web for further current reports – up to five for “URL”, six for “Manuell”, 15 for “Suchbegriff”. Slow sites don’t hold things up: after about 20 seconds it continues with the sources it has.',
                'Writing: the strongest model writes the article in one pass, directly in German from the original sources, in the HKCM style and following the formalia. The category is one of the seven CMS categories; an FAQ is written only with “FAQ erzeugen”.',
                'Checking: formats are normalised (Prozent, US-Dollar, numbers, dashes), the formalia are checked, and numbers, dates and quotes are compared with the sources. Clear errors are corrected automatically; the log shows “Faktencheck fertig (…)”, the “Faktencheck” card every finding.',
                'Cover image: a news picture as agencies like Reuters or dpa would run it with the story – bright and current, with the subject’s name large in the picture (a company’s logo on its headquarters, an index’s or commodity’s name on a screen) and, when the story is about a price, a chart on a real screen whose course follows the article – 16:9 in 4K at 3840 × 2160 pixels (JPEG). The subject sits in the right half; the left stays calm because hkcm.com sets the white headline there. No HKCM logo – HKCM never puts one on a cover. People only from behind, at a distance or out of focus, no identifiable real persons; no legible figures, only the name.',
                'The job runs on the server. Other tools can be opened meanwhile; only reloading or closing the browser tab loses the connection.',
            ],
            tips: [
                'Blocked page or paywall? Go straight to “Manuell” and paste the text.',
                'For “Suchbegriff”, ask a specific question (“Warum fällt die Nvidia-Aktie heute?”) rather than a single keyword.',
                'Links from Breaking News and Newsflash, including Google News links, work directly.',
                'After major edits, run the text through the Formalia Check again.',
            ],
            troubleshooting: [
                {
                    q: '“Der Artikel unter der URL konnte nicht geladen werden …”',
                    a: 'The site blocks automated access or has a paywall. Open the article in your browser, copy the text and paste it via the “Manuell” input.',
                },
                {
                    q: 'The source search timed out',
                    a: 'Some sources were too slow. Start again or use the “Manuell” input.',
                },
                {
                    q: 'No usable text came from the sources',
                    a: 'The pages returned nothing readable, e.g. only cookie notices. Choose another URL or use “Manuell”.',
                },
                {
                    q: '“Der Server wurde neu gestartet, der Auftrag ist verloren …”',
                    a: 'The server restarted during the run. Just create the article again.',
                },
                {
                    q: '“Der Artikel konnte nicht gestartet werden …” or “Anmeldung abgelaufen”',
                    a: 'Usually the sign-in has expired: “Erneut anmelden” in the red notice at the top, then start again.',
                },
                {
                    q: 'The badge shows FAIL',
                    a: 'The finding names the rule and the spot, e.g. a percent sign or “10.000” instead of “10 000”. Fix it with “Bearbeiten”.',
                },
                {
                    q: 'The article has no FAQ',
                    a: '“FAQ erzeugen” wasn’t ticked – without it no FAQ is written. Tick it and create the article again.',
                },
            ],
            related: ['formalia', 'seo', 'find_similar', 'infographics'],
        },

        headlines: {
            title: 'Headlines Generator',
            tagline:
                'A Google News topic page becomes German short news automatically – a headline plus three sentences, often around 50 per run.',
            badge: 'Short news in bulk',
            time: 'approx. 1.5–3 min',
            whenToUse: [
                'For a news overview on one topic, such as business, markets or a sector.',
                'For newsletters, tickers or social media when many short items are needed.',
            ],
            whatYouGet: [
                'Cards with a German headline (up to 60 characters) and a three-sentence summary.',
                'The source for each card; all links found are listed on the right (“Gefundene Artikel”, found articles).',
                'A Word export with all items, in German or English (HKCM_Headlines_<date>.docx).',
            ],
            steps: [
                {
                    title: 'Paste a topic page',
                    desc: 'The “Google-News-Seite” field is pre-filled with the business section. For another topic, open news.google.com, go to the topic and paste the address from the browser bar. Only Google News links work.',
                },
                {
                    title: 'Click “Headlines erstellen”',
                    desc: 'The progress bar names the phase: “Schlagzeilen werden gesammelt …” (collecting), “Relevanz wird geprüft (KI) …” (relevance check), “Dubletten werden zusammengefasst (KI) …” (merging duplicates), “Geschichten werden recherchiert und geschrieben …” (research and writing). The timestamped “Protokoll” (log) runs below. “Abbrechen” next to the bar cancels the run.',
                },
                {
                    title: 'Read the cards',
                    desc: 'Items appear one by one under “Kurzmeldungen” (e.g. “12 von 48 erstellt”). Click a card to expand its summary.',
                },
                {
                    title: 'Edit and use',
                    desc: 'Per card: “Bearbeiten” (edit) makes headline and text editable; select a passage and use “Mit KI bearbeiten” (edit with AI) for AI help; “Fertig” finishes. “Kopieren” copies the item, “Quelle öffnen” opens the original article.',
                },
                {
                    title: 'Export',
                    desc: '“Word” (“DE” or “EN”) downloads all cards as “HKCM Headlines” – headline, summary and source link for each item.',
                },
            ],
            options: [
                { name: 'Google-News-Seite', desc: 'A Google News topic or section page; the X (“Feld leeren”) clears the field.' },
                { name: '“Abbrechen”', desc: 'Cancels the run; shown next to the progress bar while it runs.' },
                { name: '“Word” – “DE” / “EN”', desc: 'All displayed cards as a Word file, in German or translated into English.' },
                { name: 'Buttons per card', desc: '“Bearbeiten” / “Fertig”, “Mit KI bearbeiten”, “Kopieren” and “Quelle öffnen”.' },
            ],
            behindTheScenes: [
                'The topic page is read; a model removes irrelevant items and merges duplicate stories.',
                'Stories already turned into short news in the last ten days are skipped – the log then reports “… Geschichte(n) übersprungen: schon in den letzten 10 Tagen verarbeitet.” (skipped, already processed).',
                'For each story the model researches with web search and writes headline and text in one step; if nothing usable comes back, it retries once.',
            ],
            tips: [
                'Specific topic pages, such as a sector, give more focused items than the general business page.',
                'One run in the morning and one in the afternoon is enough – another run only shows what’s new.',
                'There’s no delete button: remove unwanted items in the Word document.',
                'Reloading loses the cards – export first. Switching tools keeps them.',
            ],
            troubleshooting: [
                {
                    q: '“Fertig (keine neuen Geschichten)”',
                    a: 'Every story on the page was already processed in the last ten days. Try again later or use another topic page.',
                },
                {
                    q: 'Only a few cards',
                    a: 'The topic page had few relevant or new stories. Choose a broader page.',
                },
                {
                    q: 'A card is empty or thin',
                    a: 'The research found nothing reliable. Read the original via “Quelle öffnen” or leave the item out.',
                },
            ],
            related: ['crypto_headlines', 'single_headline', 'newsflash'],
        },

        crypto_headlines: {
            title: 'Crypto Headlines',
            tagline: 'One click delivers current crypto short news in German – from Google News Crypto, CoinDesk and The Block.',
            badge: 'Crypto only',
            time: 'approx. 1–2 min',
            whenToUse: [
                'For the daily crypto roundup without hunting for a topic page.',
                'For crypto newsletters or tickers.',
            ],
            whatYouGet: [
                'Cards with a German headline and three sentences, each with its source.',
                'A Word export with all items, in German or English (HKCM_Crypto_Headlines_<date>.docx).',
            ],
            steps: [
                {
                    title: 'Click “Headlines erstellen”',
                    desc: 'No link needed – the tool fetches stories from Google News Crypto, CoinDesk and The Block.',
                },
                {
                    title: 'Follow the progress',
                    desc: 'The same phases as the Headlines Generator: collect, filter for relevance, merge duplicates, research and write. “Abbrechen” next to the progress bar cancels.',
                },
                {
                    title: 'Read and edit the cards',
                    desc: 'Expand a card. “Bearbeiten”, “Mit KI bearbeiten”, “Kopieren” and “Quelle öffnen” work as in the Headlines Generator.',
                },
                {
                    title: 'Export',
                    desc: '“Word” (“DE” or “EN”) downloads all items.',
                },
            ],
            options: [
                { name: '“Headlines erstellen”', desc: 'Starts the run without further input.' },
                { name: '“Abbrechen” / “Word”', desc: 'Cancel, or all cards as a Word file in German or English.' },
            ],
            behindTheScenes: [
                'Sources are Google News Crypto, CoinDesk and The Block. A model filters for relevance and merges duplicate stories.',
                'Stories already turned into crypto items in the last ten days are skipped – independently of the Headlines Generator.',
                'Each story is researched with web search and written in one step.',
            ],
            tips: [
                'For automatic crypto newsflashes without clicking, use Newsflash with the “Krypto” filter.',
                'Check prices before publishing – they move fast in crypto.',
            ],
            troubleshooting: [
                {
                    q: '“Fertig (keine neuen Geschichten)”',
                    a: 'All current crypto stories were already processed in the last ten days. Try again later.',
                },
            ],
            related: ['headlines', 'newsflash', 'single_headline'],
        },

        single_headline: {
            title: 'Single Headline',
            tagline: 'Paste an article link – after 10 to 30 seconds you get a German HKCM headline and a short summary.',
            badge: 'Quick',
            time: '10–30 s',
            whenToUse: [
                'For a single story, e.g. for social media, push or a ticker.',
                'To grasp quickly what an English article is about.',
            ],
            whatYouGet: [
                'A German headline (up to 60 characters) and a three-sentence summary, with a link to the source.',
                'A Word export in German or English (HKCM_Headlines_<date>.docx).',
            ],
            steps: [
                {
                    title: 'Paste a link',
                    desc: 'Paste the link to a news article, English or German. Google News links work too.',
                },
                {
                    title: 'Click “Schlagzeile erstellen”',
                    desc: 'Or press Enter. The display shows each step; the result is ready after 10 to 30 seconds.',
                },
                {
                    title: 'Review the result',
                    desc: '“Kurzmeldung” shows headline, summary and “Quelle öffnen”. The “Per Websuche recherchiert” label (researched by web search) means the page wasn’t readable and the item is based on research into the topic – check the facts extra carefully.',
                },
                {
                    title: 'Edit (optional)',
                    desc: '“Bearbeiten” makes both editable. Select a passage, click “Mit KI bearbeiten”, enter an instruction, click “Vorschlag erstellen” (create suggestion), then “Übernehmen” (apply). “Rückgängig” reverts, “Fertig” finishes.',
                },
                {
                    title: 'Use it',
                    desc: '“Kopieren” copies headline and text; “Word” (“DE” or “EN”) in the card header downloads both as a Word file.',
                },
            ],
            options: [
                { name: 'Article URL', desc: 'Any freely readable news article, English or German.' },
                { name: '“Per Websuche recherchiert”', desc: 'Shows that the page wasn’t readable and the topic was researched instead.' },
            ],
            behindTheScenes: [
                'The page is read directly first. If it’s blocked or too short, GPT researches the topic with a web search; a browser is the last resort.',
                'Google News links are resolved to the original article first.',
                'If the page only yields cookie notices or no recognisable content, the tool stops with a message instead of guessing.',
            ],
            tips: [
                'Use the publisher’s own link where possible.',
                'For a full article, give the same link to the Article Generator.',
            ],
            troubleshooting: [
                {
                    q: 'The Google News link can’t be resolved',
                    a: 'Open the link in your browser and paste the publisher’s address.',
                },
                {
                    q: 'The page is blocked or behind a paywall',
                    a: 'Use a freely available report on the same topic, or the Article Generator with the “Manuell” (manual) input.',
                },
                {
                    q: 'Only cookie or consent text was found',
                    a: 'The site shows automated visitors only its cookie banner. Choose another source.',
                },
            ],
            related: ['headlines', 'generator'],
        },

        revenue: {
            title: 'Revenue News',
            tagline:
                'Quarterly results as a finished HKCM article: report as link or PDF, figures straight from the report, analyst estimates by research, a results table and TradingView widgets.',
            badge: 'Earnings',
            time: 'approx. 1.5–2 min',
            whenToUse: [
                'In earnings season, as soon as a company publishes its results.',
                'When revenue and earnings per share should be compared with analyst estimates.',
            ],
            whatYouGet: [
                'The figures from the report: revenue, earnings per share (EPS), free cash flow and operating cash flow – with the estimate where available.',
                'An article with SEO fields, teaser, “Das Wichtigste in Kürze”, a results table (actual vs. estimate, “übertroffen” or “verfehlt”, beat or missed) and sections on the figures, highlights, assessment and outlook.',
                'An Elliott wave note with link, a “Leseempfehlung” (reading tip), TradingView chart and fundamentals.',
                'Export as Word – with or without TradingView, in German or English – and as HTML.',
            ],
            steps: [
                {
                    title: 'Choose the source',
                    desc: '“URL” and the address under “URL des Berichts” – the press release or investor-relations page –, or “PDF” and “PDF-Datei auswählen” for the report (up to 30 MB). Optionally enter the name under “Unternehmen (optional)”.',
                },
                {
                    title: 'Click “Artikel erstellen”',
                    desc: 'Top right. Progress and the “Protokoll” (log) show the steps: read the source, extract the figures, research the analyst consensus, write the article, apply the HKCM style, add promo, reading tip and fact-check. “Abbrechen” next to the progress bar cancels.',
                },
                {
                    title: 'Check the figures',
                    desc: 'Compare the cards under “Kennzahlen” (revenue, EPS, free cash flow, operating cash flow, each with “Schätzung” for the estimate) with the report. If they’re right, the table in the article is right too.',
                },
                {
                    title: 'Read the article',
                    desc: '“Artikelvorschau” (article preview) shows word count, ticker, SEO fields and the article. Below the table it says where the estimates come from (“Schätzungen: Analystenkonsens laut …”) or that none were found. “Mobile Vorschau” shows the phone view.',
                },
                {
                    title: 'Edit (optional)',
                    desc: '“Bearbeiten” makes SEO fields, teaser, key points, subheadings, texts and the reading tip editable. Select a passage, then “Mit KI bearbeiten”, “Vorschlag erstellen” and “Übernehmen”. “Rückgängig” reverts, “Fertig” finishes. Table, Elliott block, promo and widgets stay fixed.',
                },
                {
                    title: 'Export',
                    desc: '“Word” includes the TradingView widgets, “Word ohne TradingView” (without TradingView) is for the CMS – each as “DE” or “EN”; “HTML kopieren” copies the article as HTML. The embed codes are under “TradingView-Einbettungscodes”.',
                },
            ],
            options: [
                { name: 'URL / PDF', desc: 'Press release (e.g. GlobeNewswire or the IR page) or the report as a PDF.' },
                { name: 'Unternehmen (optional)', desc: 'Helps with ambiguous names and with matching the ticker.' },
                { name: '“Word ohne TradingView”', desc: 'Word file without widgets – for systems that can’t display them.' },
                { name: '“HTML kopieren”', desc: 'Copies the article as HTML – formatted for editors, as source for HTML fields.' },
            ],
            behindTheScenes: [
                'The figures come only from the report itself – including whether earnings per share are adjusted or GAAP.',
                'GPT looks up the analyst estimates with a targeted web search, matched to fiscal quarter, period end and EPS basis. Beat or miss is then calculated, not guessed.',
                'The article is written from these figures; formats are then normalised and the text is checked against the report.',
            ],
            tips: [
                'The best source is the official press release or the report PDF – not a news article about it.',
                '“N/A” for an estimate means no reliable consensus was found. The article then makes no comparison.',
                'Check reports in euros or under IFRS extra carefully – the tool has mostly been tested on US reports.',
                'Reloading loses the result – export first. Switching tools keeps it.',
            ],
            troubleshooting: [
                {
                    q: 'A figure is wrong',
                    a: 'Check the report and correct the text via “Bearbeiten”. The table can’t be edited; if its values are wrong, regenerate from the report PDF.',
                },
                {
                    q: 'No analyst estimates found',
                    a: 'Below the table it then says “Kein Analystenkonsens gefunden”. This happens with smaller stocks or unusual period ends; the article skips the comparison.',
                },
                {
                    q: 'The CMS doesn’t show the TradingView widgets',
                    a: 'Use “Word ohne TradingView”.',
                },
                {
                    q: 'The page won’t load',
                    a: 'Download the report as a PDF and upload it via “PDF”.',
                },
            ],
            related: ['generator', 'formalia', 'seo'],
        },

        formalia: {
            title: 'Formalia Check',
            tagline:
                'Checks a finished article against the HKCM formalia – lengths, structure, number, percent and currency formats, gendered language – and smooths the style on request.',
            badge: 'Quality control',
            time: 'check instant · style approx. 30 s',
            whenToUse: [
                'Before publishing any text that didn’t come from the tools or was heavily edited.',
                'To bring external texts in line with the house rules.',
            ],
            whatYouGet: [
                'A badge: Bestanden, or the number of FAIL and WARN findings.',
                'Tiles with the target range, green when the value fits: H1 (20–90 characters), Teaser (90–170 characters, 200 at most), Text (500–1000 words), Kernpunkte (3), FAQ (0–4, optional) and Meta-Titel (30–60 characters).',
                'Every finding with its rule, location and a text excerpt.',
                'A Word export in German or English (HKCM_Artikel_<date>.docx).',
            ],
            steps: [
                {
                    title: 'Add the text',
                    desc: 'Easiest via “Word hochladen” (upload Word, .docx) – headline, teaser, key points, body, FAQ and SEO fields are recognised. Or fill in the fields one by one: headline (H1), teaser, key points (one per line), body (subheadings with ##), FAQ (“Frage: …” and “Antwort: …”), meta title, category, meta description and tags.',
                },
                {
                    title: 'Detect the structure (when pasting)',
                    desc: 'After pasting from Word, click “Struktur erkennen” (detect structure) so subheadings are recognised. If all key points are on one line, “Punkte trennen” (split points) separates them.',
                },
                {
                    title: 'Click “Formalia prüfen”',
                    desc: 'Top right. The result is instant: Bestanden, or e.g. “2 FAIL” and “3 WARN”, with each finding and its location below.',
                },
                {
                    title: 'Fix',
                    desc: 'Change the passages directly in the fields. For AI help, select a passage or click into a field and choose “Mit KI bearbeiten” (edit with AI) – with nothing selected, the suggestion replaces the whole field. “Stil anpassen” (adjust style) smooths language and formats across the whole article – while it runs, the fields are locked; “Stil angepasst” lists the changes. “Rückgängig” (undo) reverts.',
                },
                {
                    title: '“Erneut prüfen” (check again) until no FAIL is left',
                    desc: 'FAIL must be fixed before publishing, WARN is a recommendation.',
                },
                {
                    title: 'Export',
                    desc: '“Word” (“DE” or “EN”) downloads the article as a Word file; headline and body are required.',
                },
            ],
            options: [
                { name: '“Word hochladen”', desc: 'Reads a .docx file and distributes it across the fields.' },
                { name: '“Struktur erkennen”', desc: 'After pasting, marks the subheadings with ## and recognises key points and FAQ.' },
                { name: '“Punkte trennen”', desc: 'Splits key points stuck together into separate lines; appears next to “Kernpunkte” when all points are on one line.' },
                { name: '“Formalia prüfen” / “Erneut prüfen”', desc: 'Checks the rules; the result is instant.' },
                { name: '“Stil anpassen”', desc: 'Smooths language and formats with AI, changing the text. Until the AI is done, the fields are locked, so nothing you type is overwritten.' },
                { name: '“Mit KI bearbeiten”', desc: 'Edits the selected passage in the field clicked last, following an instruction; with nothing selected, the whole field.' },
                { name: '“Rückgängig”', desc: 'Reverts the latest changes step by step.' },
            ],
            behindTheScenes: [
                'FAIL (must be fixed): gendered forms, a dot as thousands separator (“10.000” instead of “10 000”), implausible magnitudes (“14 191 Milliarden”), a percent sign instead of “Prozent”, currency symbols ($, €, £, ¥), personal calls to action such as “jetzt kaufen”, headline over 90 characters, teaser over 200 characters, meta title over 60 and meta description over 160 characters, fewer than three key points, body under 500 words.',
                'WARN (recommendation): “Dollar” instead of “US-Dollar”, a decimal comma instead of a point (“2,5” instead of “2.5”), abbreviations such as “5B”, “—” or “ - ” instead of “ – ”, addressing the reader as “du”, sentences over 40 words, very short or long paragraphs and subheadings, more than three key points, more than four FAQ entries or a question without an answer, a missing or unknown category, body over 1000 words, teaser over 170 characters, short headline, teaser or meta fields.',
                'The FAQ is optional: no FAQ, no finding. The category must be one of the seven CMS categories: Global Markets, Deutschland, Finanzbildung, Trendmärkte, Technologie, Kryptowährungen, Rohstoffe und Energie.',
                'The check is rule-based: the same text always gives the same result, without AI.',
            ],
            tips: [
                '“Word hochladen” preserves the structure better than copy and paste.',
                'Check and fix specific findings first, “Stil anpassen” afterwards – it rewrites the whole text.',
                'The tiles are guides; the FAIL findings are what counts.',
            ],
            troubleshooting: [
                {
                    q: '“Bitte zuerst den Artikeltext eingeben.” (enter the article text first)',
                    a: 'The body field is empty. Paste the text or use “Word hochladen”.',
                },
                {
                    q: '“Titel und Artikeltext sind erforderlich.” (title and article text are required)',
                    a: 'Headline and body must be filled in for the export.',
                },
                {
                    q: 'Subheadings aren’t recognised',
                    a: 'They must start with ##. Click “Struktur erkennen” or upload the Word file.',
                },
            ],
            related: ['seo', 'find_similar', 'generator'],
        },

        seo: {
            title: 'SEO Optimization',
            tagline: 'Analyses an article’s chances in search engines and applies selected improvements automatically.',
            badge: 'Search engines',
            time: 'approx. 20–60 s',
            whenToUse: [
                'Before publishing an article that should be found on Google.',
                'To improve meta title, meta description, subheadings and search terms.',
            ],
            whatYouGet: [
                'Suggestions by priority: “Kritisch” (critical), “Empfohlen” (recommended) and “Optional”.',
                'The selected suggestions applied directly to the text.',
                'A Word export in German or English (HKCM_Artikel_<date>.docx).',
            ],
            steps: [
                {
                    title: 'Add the text',
                    desc: '“Word hochladen” (.docx) or fill in the fields one by one: headline, teaser, key points, body, FAQ and SEO fields. After pasting, “Struktur erkennen” helps.',
                },
                {
                    title: 'Click “SEO analysieren”',
                    desc: 'Top right. The suggestions appear under “SEO-Vorschläge” (SEO suggestions), sorted into “Kritisch”, “Empfohlen” and “Optional”.',
                },
                {
                    title: 'Pick suggestions',
                    desc: 'Tick the suggestions wanted – “Kritisch” first.',
                },
                {
                    title: 'Click “Verbesserungen übernehmen”',
                    desc: 'The AI applies the selection to the text (“Verbesserungen übernommen.” (improvements applied)); while it works, the fields are locked. Had it cut the text heavily, the text stays as it was and a notice says so. Check the result in the fields; “Mit KI bearbeiten” helps with single passages.',
                },
                {
                    title: 'Export',
                    desc: '“Word” (“DE” or “EN”) downloads the article as a Word file; headline and body are required.',
                },
            ],
            options: [
                { name: '“Word hochladen” / “Struktur erkennen”', desc: 'Take the text from a .docx file, or recognise the structure after pasting.' },
                { name: '“SEO analysieren”', desc: 'Analyses the article and returns suggestions.' },
                { name: '“Verbesserungen übernehmen”', desc: 'Applies the ticked suggestions to the text.' },
                { name: '“Mit KI bearbeiten”', desc: 'Edits the selected passage in the field clicked last, following an instruction; with nothing selected, the whole field.' },
            ],
            behindTheScenes: [
                'A fast model rates headline, teaser, key points, body, FAQ and meta fields for German-language financial searches while keeping the HKCM style.',
                'The strongest model applies the changes – only for the ticked points; the rest of the text stays as it is.',
            ],
            tips: [
                'Meta title 30–60 characters, meta description 100–160.',
                'After “Verbesserungen übernehmen”, run the text through the Formalia Check again.',
                'Don’t take every suggestion: readability beats keywords.',
            ],
            troubleshooting: [
                {
                    q: '“Bitte zuerst den Artikeltext eingeben.” (enter the article text first)',
                    a: 'The body field is empty. Paste the text or use “Word hochladen”.',
                },
                {
                    q: '“Bitte mindestens einen Vorschlag auswählen.” (select at least one suggestion)',
                    a: 'Tick at least one suggestion before “Verbesserungen übernehmen”.',
                },
            ],
            related: ['formalia', 'find_similar'],
        },

        infographics: {
            title: 'News-Grafiken',
            tagline:
                'Creates infographics in the HKCM design for an existing article – each shows one key aspect, laid out on the spec’s 1500 × 844 grid and delivered in 4K (3840 × 2160 pixels), with consistent logo and colours.',
            badge: 'Graphics',
            time: 'approx. 1 min',
            whenToUse: [
                'When an article contains figures, sequences or comparisons that a graphic explains better.',
                'For social media and article images in the consistent HKCM look.',
            ],
            whatYouGet: [
                'Two to three graphics (or a fixed number from 1 to 4), each on one key aspect of the article.',
                'For each graphic: key aspect, layout, main element, caption and any review notes.',
                'Each graphic as a PNG, one at a time or all at once.',
            ],
            steps: [
                {
                    title: 'Add the article',
                    desc: 'Paste the article text (subheadings with ##) or use “Word hochladen” (upload Word). Optionally fill in “Titel” (title) and “Quelle” (source) – the source is credited on the graphics.',
                },
                {
                    title: 'Choose the number',
                    desc: '“Automatisch (2 bis 3)” (automatic, 2 to 3) or a fixed number from 1 to 4.',
                },
                {
                    title: 'Click “Grafiken erstellen”',
                    desc: 'This takes about half a minute to a minute; the bar shows how far the run is, “Abbrechen” ends it. You may leave the page meanwhile – the run carries on.',
                },
                {
                    title: 'Review',
                    desc: 'Each card shows the graphic with key aspect, layout and caption; a click enlarges it. Notes on the card show what should be checked.',
                },
                {
                    title: 'Download',
                    desc: '“PNG” downloads one graphic, “Alle herunterladen” downloads all.',
                },
            ],
            options: [
                { name: 'Titel (optional)', desc: 'The article’s headline; helps choose the key aspects.' },
                { name: 'Quelle (optional)', desc: 'Shown as the source credit on the graphics.' },
                { name: 'Anzahl', desc: '“Automatisch (2 bis 3)” or 1 to 4.' },
                { name: '“Word hochladen”', desc: 'Takes headline and text from a Word file (.docx).' },
            ],
            behindTheScenes: [
                'A model picks the key aspects and fitting displays – such as a process, timeline, line, bars, share, key figure or quote – and uses only numbers that appear in the text.',
                'The layout is code: every area gets content of its own, charts are drawn from the values – close values as dots on an axis whose start is stated –, no figure appears twice, and long words break at a syllable.',
                'The graphics are laid out in a browser, not drawn by an image model. That’s why figures, text, colours, fonts, logo and dimensions are always exact.',
                'As in the newsroom’s templates: a topic line above the headline, an icon beside every key figure and point, green for a gain and red for a loss or a risk. Beside a wide headline sits a small 3D illustration of the subject – drawn by the image model, without text or figures; if it fails, the graphic comes without one.',
            ],
            tips: [
                'The Article Generator can create graphics directly – tick “News-Grafiken erstellen” – and includes them in the Word report.',
                'Texts with concrete figures give the most telling graphics.',
                'Compare every number on a graphic with the article once more.',
            ],
            troubleshooting: [
                {
                    q: '“Grafiken erstellen” can’t be clicked',
                    a: 'The text field is empty. Paste the text or upload a Word file.',
                },
                {
                    q: 'The graphics couldn’t be created',
                    a: 'Try again. If that doesn’t help, use a shorter text or a fixed number.',
                },
            ],
            related: ['generator', 'formalia'],
        },

        find_similar: {
            title: 'Find Similar Links',
            tagline:
                'Finds fitting links for an article – on the web, in the HKCM archive, among HKCM products and in the German press – and inserts the chosen ones with AI.',
            badge: 'Linking',
            time: 'approx. 10–40 s per search',
            whenToUse: [
                'Before publishing, to add internal HKCM links and fitting products.',
                'To enrich an article with further reading.',
            ],
            whatYouGet: [
                'Four separate result lists: web articles, HKCM news, HKCM products and German news.',
                'A preview in which the AI has placed the chosen links at fitting spots.',
                'A Word export in German or English (HKCM_Artikel_<date>.docx).',
            ],
            steps: [
                {
                    title: 'Add the text',
                    desc: '“Word hochladen” (.docx) or fill in the fields one by one. After pasting, “Struktur erkennen” helps.',
                },
                {
                    title: 'Search',
                    desc: 'In the “Ähnliche Artikel und Produkte” (similar articles and products) area, click “Web-Artikel suchen”, “HKCM-News suchen”, “HKCM-Produkte suchen” or “Deutsche Nachrichten suchen”. Each search runs on its own and fills its own list.',
                },
                {
                    title: 'Pick links',
                    desc: 'Tick fitting results; the count of selected links is shown below. “Auswahl aufheben” clears the choice.',
                },
                {
                    title: 'Preview',
                    desc: '“Links einsetzen” (insert links) shows the text with the links in place under “Vorschau” (preview); while the AI sets them, the fields are locked. If it fits, click “Übernehmen” (“Links eingefügt.” (links inserted)); otherwise “Verwerfen” (discard). If the text was changed after that, “Übernehmen” applies nothing and says so – click “Links einsetzen” again.',
                },
                {
                    title: 'Export',
                    desc: '“Word” (“DE” or “EN”) downloads the enriched article as a Word file.',
                },
            ],
            options: [
                { name: '“Web-Artikel suchen”', desc: 'Current reports from the web on the same topic.' },
                { name: '“HKCM-News suchen”', desc: 'Fitting articles from the HKCM archive.' },
                { name: '“HKCM-Produkte suchen”', desc: 'Fitting HKCM products, e.g. for a promo note.' },
                { name: '“Deutsche Nachrichten suchen”', desc: 'German-language financial press on the topic.' },
                { name: '“Links einsetzen” / “Übernehmen”', desc: 'Review first, then apply – nothing changes without “Übernehmen”.' },
            ],
            behindTheScenes: [
                'Search terms are derived from the headline and body. HKCM news comes from the HKCM archive, products from the HKCM product catalogue, web and German news from the news search – with GPT web search as a fallback.',
                'When inserting, the AI chooses fitting anchor texts and spots in the text; the rest of the text stays unchanged.',
            ],
            tips: [
                'One or two internal HKCM links per article are usually enough.',
                'Check the anchor texts in the preview – they should read naturally in the sentence.',
                'Afterwards, run the text through the Formalia Check again.',
            ],
            troubleshooting: [
                {
                    q: 'A list stays empty',
                    a: 'That source had nothing fitting for this topic. Use the other searches or make the headline more specific.',
                },
                {
                    q: '“Titel und Artikeltext sind erforderlich.” (title and article text are required)',
                    a: 'Headline and body must be filled in for the export.',
                },
            ],
            related: ['seo', 'formalia'],
        },
    },

    troubleshooting: {
        title: 'Troubleshooting',
        intro: 'The most common messages and what to do. Tool-specific cases are also on each tool’s page.',
        sections: [
            {
                title: 'Sign-in',
                items: [
                    {
                        q: '“Anmeldung abgelaufen” (sign-in expired)',
                        a: 'Your sign-in has expired. Click “Erneut anmelden” (sign in again) in the red notice at the top and sign in in the window – the page, your input and running jobs stay, and everything works again afterwards. If no window opens, allow pop-ups for the site, or click “Abmelden” and sign in again.',
                    },
                    {
                        q: '“… konnte nicht gestartet werden …” (could not be started)',
                        a: 'Usually an expired sign-in too: “Erneut anmelden”, then start again.',
                    },
                ],
            },
            {
                title: 'Sources and websites',
                items: [
                    {
                        q: 'A page won’t load or is blocked',
                        a: 'The site blocks automated access or has a paywall. Open the article in your browser, copy the text and paste it yourself (Article Generator: “Manuell”) – or use another source.',
                    },
                    {
                        q: 'A Google News link doesn’t work',
                        a: 'The Article Generator and Single Headline resolve Google News links themselves. If that fails, open the link in your browser and use the publisher’s address.',
                    },
                    {
                        q: 'The result is based on cookie text or seems off-topic',
                        a: 'The site only showed automated visitors a cookie banner. Choose another source or paste the text yourself.',
                    },
                ],
            },
            {
                title: 'Running jobs',
                items: [
                    {
                        q: 'The result is gone after reloading',
                        a: 'Switching tools keeps every result; reloading the page does not (except Newsflash). Export before reloading.',
                    },
                    {
                        q: 'The page was reloaded during a run',
                        a: 'The page loses its connection to the job. Start the run again. In Headlines and Crypto Headlines, the new run may skip stories the interrupted one already processed.',
                    },
                    {
                        q: '“Der Server wurde neu gestartet …” (the server was restarted) or “… nicht mehr erreichbar”',
                        a: 'The server restarted during the run, for example for an update. Just start again.',
                    },
                ],
            },
            {
                title: 'Results and figures',
                items: [
                    {
                        q: 'A number or name is wrong',
                        a: 'Check it against the original source and correct the text. The fact-check catches clear errors, but it doesn’t replace sign-off.',
                    },
                    {
                        q: 'The formalia badge shows FAIL',
                        a: 'FAIL must be fixed before publishing, e.g. gendered forms, “10.000” instead of “10 000”, or a percent sign. WARN is a recommendation. Fix the passage and check again.',
                    },
                    {
                        q: 'Revenue News shows “N/A” for the estimates',
                        a: 'No reliable analyst consensus was found. The article then skips the comparison.',
                    },
                ],
            },
            {
                title: 'Export',
                items: [
                    {
                        q: 'The Word export fails',
                        a: 'Try again. In Formalia Check, SEO Optimization and Find Similar Links, headline and body must be filled in.',
                    },
                    {
                        q: '“Die Übersetzung ins Englische ist fehlgeschlagen.”',
                        a: 'Click “EN” again. The translation takes a few seconds, up to a minute for long documents. The German version (“DE”) is not affected.',
                    },
                    {
                        q: 'The CMS doesn’t show TradingView widgets',
                        a: 'In Revenue News, use “Word ohne TradingView”.',
                    },
                ],
            },
        ],
    },

    glossary: {
        title: 'Glossary',
        intro: 'The key terms from editing, SEO and the tools. The definitions are in German, the desk’s working language; many also appear in the tools behind the (?) icon.',
    },
}
